import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Chip,
  Stack,
  Paper,
  Snackbar,
  IconButton,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import TagOutlinedIcon from '@mui/icons-material/TagOutlined';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { Header } from 'components/Header';
import { useServiceConfigs } from '../context/ServiceConfigContext';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import { v4 as uuidv4 } from 'uuid';
import { isTagUnique } from '../services/api';
import { formatApiErrorMessage, logApiError } from '../services/errors';
import { resizeImageFile } from '../utils/imageProcessing';

interface CustomerOwnedPhotoUploadPageProps {
  externalIdentity: string;
}

const CustomerOwnedPhotoUploadPage: React.FC<CustomerOwnedPhotoUploadPageProps> = ({ externalIdentity }) => {
  const navigate = useNavigate();
  const { state } = useLocation() as {
    state?: { metalType?: string; productType?: string; measuredSize?: string; condition?: string };
  };
  const metalType = state?.metalType;
  const productType = state?.productType;
  const measuredSize = state?.measuredSize;
  const condition = state?.condition;

  const [photo, setPhoto] = useState<File | null>(null);
  const [comment, setComment] = useState('');
  const [tagNumber, setTagNumber] = useState('');
  const [snack, setSnack] = useState<string | null>(null);
  const [tagStatus, setTagStatus] = useState<'idle' | 'checking' | 'unique' | 'duplicate' | 'error'>('idle');
  const [tagErrorMessage, setTagErrorMessage] = useState<string | null>(null);
  const [lastCheckedTag, setLastCheckedTag] = useState('');
  const [photoProcessing, setPhotoProcessing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { setPhotoUploadInfo, serviceType, photoUploadInfo } = useServiceConfigs();

  const isQuick = serviceType === 'STANDARD' ? false : true;
  const trimmedTag = tagNumber.trim();
  const hasTag = trimmedTag.length > 0;
  const isTagValidationBlocking = hasTag && (tagStatus === 'duplicate' || tagStatus === 'checking' || tagStatus === 'error');

  useEffect(() => {
    setPhoto(photoUploadInfo.photo ?? null);
    setComment(photoUploadInfo.comment ?? '');
    setTagNumber(photoUploadInfo.tag ?? '');
    if (!photoUploadInfo.tag) {
      setTagStatus('idle');
      setLastCheckedTag('');
      setTagErrorMessage(null);
    }
  }, [photoUploadInfo]);

  useEffect(() => {
    const normalized = tagNumber.trim().toUpperCase();

    if (!externalIdentity || !normalized) {
      if (lastCheckedTag !== '') {
        setLastCheckedTag('');
      }
      setTagStatus((prev) => (prev === 'idle' ? prev : 'idle'));
      setTagErrorMessage(null);
      return;
    }

    if (normalized === lastCheckedTag) {
      return;
    }

    let cancelled = false;
    const timeoutId = window.setTimeout(async () => {
      setTagStatus('checking');
      setTagErrorMessage(null);
      try {
        const unique = await isTagUnique(externalIdentity, normalized);
        if (cancelled) return;
        setLastCheckedTag(normalized);
        setTagStatus(unique ? 'unique' : 'duplicate');
        setTagErrorMessage(null);
      } catch (err) {
        if (cancelled) return;
        setLastCheckedTag(normalized);
        setTagStatus('error');
        const message = formatApiErrorMessage(err, 'Unable to validate this tag number. Please try again.');
        setTagErrorMessage(message);
        logApiError('Validating tag number', err);
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [tagNumber, externalIdentity, lastCheckedTag]);

  const handlePhotoClick = () => {
    if (photoProcessing) return;
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    e.target.value = '';
    setPhotoProcessing(true);
    try {
      const optimized = await resizeImageFile(f);
      setPhoto(optimized);
    } catch (err) {
      console.error('Failed to optimize customer-owned photo', err);
      setSnack('Unable to process this photo. Please try again.');
    } finally {
      setPhotoProcessing(false);
    }
  };

  const genQuickTag = () => {
    const generated = `QS-${uuidv4().slice(0, 8).toUpperCase()}`;
    setTagStatus('idle');
    setTagErrorMessage(null);
    setLastCheckedTag('');
    setTagNumber(generated);
    setSnack('Tag generated');
  };

  const handleSubmit = () => {
    if (photoProcessing) {
      setSnack('Please wait for the photo to finish processing.');
      return;
    }
    if (!photo) {
      setSnack('Please attach a photo before continuing.');
      return;
    }
    // if (!comment.trim()) {
    //   setSnack("Please add notes about the item's condition before continuing.");
    //   return;
    // }
    if (!isQuick && !tagNumber.trim()) {
      setSnack('Please enter a tag number before continuing.');
      return;
    }

    const normalizedTag = trimmedTag.toUpperCase();
    if (normalizedTag && tagStatus === 'checking') {
      setSnack('Please wait for the tag validation to complete.');
      return;
    }
    if (normalizedTag && tagStatus === 'duplicate') {
      setSnack('This tag number is already in use. Please enter a different tag.');
      return;
    }
    if (normalizedTag && tagStatus === 'error') {
      setSnack(tagErrorMessage || 'Unable to verify the tag number. Please try again.');
      return;
    }
    let finalTag = normalizedTag;
    if (!normalizedTag && isQuick) {
      finalTag = `QS-${uuidv4().slice(0, 8).toUpperCase()}`;
      setTagStatus('idle');
      setLastCheckedTag('');
      setTagNumber(finalTag);
    }

    setPhotoUploadInfo({ photo, comment: comment.trim(), tag: finalTag });
    navigate('/service', { state: { metalType, productType, measuredSize, condition } });
  };

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <Header
        title={<Typography variant="h6">Guest Owned</Typography>}
        subtitle="Photo & Notes"
        showBackButton
        onBack={() => navigate(-1)}
        sticky
        align="left"
        elevateOnScroll
      />

      {/* Context chips */}
      <Card
        sx={(theme) => ({
          mx: 2,
          mt: 2,
          borderRadius: 3,
          overflow: 'hidden',
          border: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
          boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
        })}
      >
        <CardContent sx={{ py: 2 }}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip
              label={`Metal: ${metalType ?? '—'}`}
              variant="outlined"
              sx={{ borderRadius: 2 }}
            />
            <Chip
              label={`Product: ${productType ?? '—'}`}
              variant="outlined"
              sx={{ borderRadius: 2 }}
            />
            <Chip
              label={`Measured Size: ${measuredSize ?? '—'}`}
              variant="outlined"
              sx={{ borderRadius: 2 }}
            />
            <Chip
              label={`Condition: ${condition ?? '—'}`}
              variant="outlined"
              sx={{ borderRadius: 2 }}
            />
            <Chip
              icon={serviceType === 'QUICK' ? <BoltOutlinedIcon /> : <ScheduleOutlinedIcon />}
              label={serviceType === 'QUICK' ? 'Quick Service' : 'Standard Service'}
              variant="outlined"
              color={serviceType === 'QUICK' ? 'primary' : 'default'}
              sx={{ borderRadius: 2 }}
            />
          </Stack>
        </CardContent>
      </Card>

      {/* Tag capture */}
      <Card
        sx={(theme) => ({
          mx: 2,
          mt: 2,
          borderRadius: 3,
          overflow: 'hidden',
          border: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
          boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
        })}
      >
        <CardContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            Tag Number {isQuick ? '(Optional for Quick Services)' : ''}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            This tag helps us associate the physical item with the service request.
          </Typography>

          <TextField
            fullWidth
            placeholder={isQuick ? 'Tag number (optional for Quick Services)' : 'Enter tag number'}
            label="Tag Number"
            value={tagNumber}
            onChange={(e) => {
              const next = e.target.value.toUpperCase();
              if (next.trim() !== lastCheckedTag) {
                setTagStatus('idle');
              }
              setTagNumber(next);
            }}
            inputProps={{ maxLength: 40, style: { textAlign: 'center' } }}
            sx={(theme) => ({
              mt: 1,
              maxWidth: 600,
              mx: 'auto',
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.background.paper, 0.6),
                transition: 'box-shadow .2s ease',
                '&:hover': { boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.08)}` },
                '&.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.16)}` },
              },
            })}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <TagOutlinedIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: isQuick ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={genQuickTag} aria-label="Generate tag">
                    <AutorenewIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
            error={(!isQuick && !hasTag) || (hasTag && (tagStatus === 'duplicate' || tagStatus === 'error'))}
            FormHelperTextProps={{ sx: { textAlign: 'center' } }}
            helperText={
              <Box component="span">
                <Typography component="span" variant="caption" sx={{ display: 'block' }}>
                  {isQuick
                    ? 'Optional for Quick services — we can generate one for you.'
                    : 'Required for Standard services.'}
                </Typography>
                {hasTag && tagStatus === 'checking' && (
                  <Typography
                    component="span"
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block' }}
                  >
                    Checking tag availability…
                  </Typography>
                )}
                {hasTag && tagStatus === 'unique' && (
                  <Typography
                    component="span"
                    variant="caption"
                    color="success.main"
                    sx={{ display: 'block' }}
                  >
                    Tag number is available.
                  </Typography>
                )}
                {hasTag && tagStatus === 'duplicate' && (
                  <Typography
                    component="span"
                    variant="caption"
                    color="error.main"
                    sx={{ display: 'block' }}
                  >
                    This tag number is already in use. Please enter a different tag.
                  </Typography>
                )}
                {hasTag && tagStatus === 'error' && (
                  <Typography
                    component="span"
                    variant="caption"
                    color="error.main"
                    sx={{ display: 'block' }}
                  >
                    {tagErrorMessage || 'Unable to validate this tag number. Please try again.'}
                  </Typography>
                )}
              </Box>
            }
          />

          {!isQuick && !tagNumber.trim() && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
              Tag number is required for Standard services.
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Photo upload */}
      <Card
        sx={(theme) => ({
          mx: 2,
          mt: 2,
          borderRadius: 3,
          overflow: 'hidden',
          border: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
          boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
        })}
      >
        <CardContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
          }}
        >
          {!photo ? (
            <>
              <Paper
                variant="outlined"
                onClick={handlePhotoClick}
                sx={(theme) => ({
                  mt: 1,
                  width: '100%',
                  maxWidth: 360,
                  height: 160,
                  borderRadius: 2,
                  borderStyle: 'dashed',
                  borderColor: alpha(theme.palette.primary.main, 0.4),
                  bgcolor: alpha(theme.palette.primary.light, 0.05),
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  transition: 'box-shadow .15s ease, border-color .15s ease',
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                  },
                  opacity: photoProcessing ? 0.5 : 1,
                  pointerEvents: photoProcessing ? 'none' : 'auto',
                })}
              >
                <CameraAltIcon />
                <Typography variant="body2" fontWeight={600}>
                  Tap to take/upload a photo
                </Typography>
              </Paper>

              <Button
                onClick={handlePhotoClick}
                startIcon={<CloudUploadIcon />}
                sx={{ textTransform: 'none', borderRadius: 2 }}
                variant="outlined"
                disabled={photoProcessing}
              >
                Choose Image
              </Button>
              {photoProcessing && (
                <Stack alignItems="center" spacing={1}>
                  <CircularProgress size={20} />
                  <Typography variant="caption" color="text.secondary">
                    Optimizing photo…
                  </Typography>
                </Stack>
              )}
            </>
          ) : (
            <>
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  maxWidth: 360,
                  borderRadius: 2,
                  overflow: 'hidden',
                }}
              >
                <img
                  src={URL.createObjectURL(photo)}
                  alt="Preview"
                  style={{ width: '100%', display: 'block' }}
                />
                <IconButton
                  onClick={() => {
                    setPhoto(null);
                  }}
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    bgcolor: 'background.paper',
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    '&:hover': { bgcolor: 'background.paper' },
                  }}
                  aria-label="Remove photo"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </>
          )}

          <input
            type="file"
            accept="image/*"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          {/* Hiding comment box */}
          {/* <TextField
            multiline
            minRows={3}
            fullWidth
            placeholder="Is item new, worn, scratched or damaged? Any additional notes that would be pertinent?"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            sx={(theme) => ({
              mt: 1,
              maxWidth: 600,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.background.paper, 0.6),
                transition: 'box-shadow .2s ease',
                '&:hover': { boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.08)}` },
                '&.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.16)}` },
              },
            })}
          /> */}
        </CardContent>
      </Card>

      {/* Action */}
      <Box mt={3} mx={2} mb={4}>
        <Button
          variant="contained"
          fullWidth
          onClick={handleSubmit}
          startIcon={<ArrowForwardIcon />}
          disabled={isTagValidationBlocking || !tagNumber.trim()}
          sx={(theme) => ({
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 700,
            '&.Mui-disabled': {
              color: theme.palette.common.white,
              backgroundColor: alpha(theme.palette.primary.main, 0.35),
            },
          })}
        >
          Select Service
        </Button>
      </Box>

      <Snackbar
        open={!!snack}
        autoHideDuration={2000}
        onClose={() => setSnack(null)}
        message={snack ?? ''}
      />

    </Box>
  );
};

export default CustomerOwnedPhotoUploadPage;
